import { Link, useLocation, useParams } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { GuideClosing } from "@/components/guides/GuideClosing";
import { LandingNav } from "@/components/landing/LandingNav";
import { Container, ContourField, Eyebrow } from "@/components/landing/primitives";
import { getGuide, GUIDE_KEYS, guideNavPath } from "@/lib/guides";
import { isLandingLocale, localePath, makeTr, pick } from "@/lib/landing/locale";
import { languageFromPath } from "@/lib/site/publicPages";

/**
 * The guide hub ("Průvodce"): every guide page with its one-line answer, so
 * each is two clicks from the home page and crawlers find them all.
 */
export default function Guides() {
  const { lang } = useParams<{ lang: string }>();
  const { pathname } = useLocation();
  const locale = isLandingLocale(lang) ? lang : (languageFromPath(pathname) ?? "cs");
  const tr = makeTr(locale);
  const navPath = guideNavPath("guides");

  return (
    <div
      lang={locale}
      className="min-h-screen overflow-x-clip bg-background text-foreground antialiased"
    >
      <LandingNav locale={locale} path={navPath} />

      <main id="obsah">
        <section aria-labelledby="guides-title" className="relative isolate overflow-hidden">
          <ContourField variant="hero" className="opacity-[0.14] dark:opacity-[0.08]" />
          <Container className="pb-10 pt-14 sm:pb-14 sm:pt-20">
            <Eyebrow className="mb-3">{tr("Průvodce", "Guides")}</Eyebrow>
            <h1
              id="guides-title"
              className="max-w-3xl text-[2rem] font-semibold leading-[1.1] tracking-tight sm:text-5xl"
            >
              {tr(
                "Doklady, platby z Fio a podklady pro účetní",
                "Documents, Fio payments and files for your accountant"
              )}
            </h1>
            <p className="mt-4 max-w-2xl text-base leading-relaxed text-muted-foreground sm:text-lg">
              {tr(
                "Co Zettl dělá, krok za krokem: od stažení plateb z Fio banky po balíček dokladů pro účetní.",
                "What Zettl does, step by step: from downloading your Fio banka payments to the bundle of documents for your accountant."
              )}
            </p>
          </Container>
        </section>

        <Container className="pb-20 sm:pb-28">
          <ul className="grid gap-4 md:grid-cols-2">
            {GUIDE_KEYS.map((key) => {
              const guide = getGuide(key);
              return (
                <li key={key}>
                  <Link
                    to={localePath(locale, guideNavPath(key))}
                    className="group flex h-full items-start justify-between gap-4 rounded-xl border bg-card p-5 transition-colors hover:bg-secondary/50"
                  >
                    <div>
                      <h2 className="text-lg font-semibold tracking-tight">
                        {pick(guide.name, locale)}
                      </h2>
                      <p className="mt-1.5 text-[15px] leading-relaxed text-muted-foreground">
                        {pick(guide.lead, locale)}
                      </p>
                    </div>
                    <ArrowRight
                      aria-hidden="true"
                      className="mt-1.5 size-4 shrink-0 text-muted-foreground transition-transform group-hover:translate-x-0.5"
                    />
                  </Link>
                </li>
              );
            })}
          </ul>
        </Container>
      </main>

      <GuideClosing locale={locale} path={navPath} />
    </div>
  );
}
